import type { SiteId } from './data/sites';

/**
 * The spinner dolphin at Maui Nui (M188): a resident of Maui Nui's shallow channels and bays, as it is read, and no
 * count. No estimate of Maui Nui's spinner dolphins alone exists. NOAA's stock assessment report for the Hawaiian
 * Islands spinner dolphins ("Revised 3/1/2019", a US government work; its PDF's p. 4) holds "No minimum population
 * estimate" for the Oʻahu/4-Islands stock, Maui Nui's, "as the most recent estimate of abundance is >8 years old":
 * aerial surveys within 25 nmi of the main islands in 1993 to 1998 gave 3,184 for three stocks together, which "It is
 * not feasible to partition ... into island-specific abundance estimates", and photographs off Oʻahu's leeward coast 160
 * in 2002 and 355 in 2007, which "likely underestimate true stock abundance". A study at Maui Nui of 51 encounters from
 * 2013 to 2022 found an "absence of population level estimates of spinner dolphins in the O'ahu/4-islands stock or
 * within the Maui Nui region of the stock" (Olson, Barber-Meyer and Currie 2026, *Front. Mamm. Sci.* 4: 1712553, p. 6,
 * CC BY), and illustrated its sums with Oʻahu's 594 (Lacey et al. 2025, p. 3), which the game does not take for Maui
 * Nui's. So the game holds none, as it held none of the tiger shark's until M197: the dolphin is in the field guide as it
 * lives, and its numbers wait for a count (open).
 */
export const SPINNER_DOLPHIN = {
  /**
   * Its days: "a distinct diurnal pattern of resting and avoiding predators inshore during the day and foraging
   * offshore at night" (Olson et al. 2026, p. 2); at Maui Nui "to not only rest in protected bays, but also to utilize
   * the relatively shallow channels for resting and traveling throughout the day" (p. 2).
   */
  restsInshoreByDay: true,
  /** Its groups at Maui Nui: "mean = 65.40 individuals, SD = 45.24" (Olson et al. 2026, p. 1), of 51 encounters. */
  groupMean: 65.4,
  groupSd: 45.24,
  /**
  * Its protection: "In 2021, to minimize impacts of tourism, National Oceanic and Atmospheric Association (NOAA)
   * implemented a 50-yard (43 meter) approach limit for humans and boats regarding spinner dolphins within two nautical
   * miles of shore (NOAA, 86 FR 53818)" (Olson et al. 2026, p. 2): the yards the rule's; its meters the paper's slip (50
   * yards are 45.72 m), not to be taken.
   */
  approachLimitYards: 50,
  /**
   * How often they are near shore (M216): Maui Nui's two recorders in the autumn of 2016, west Maui's and Lānaʻi's,
   * "each EAR location detected delphinids occurring within 1–2 km of the coastline", heard dolphins in 14.83% and
   * 14.32% of hours ("73.54% of hours did not contain dolphin signals at either location, 2.70% of hours contained
   * dolphin signals at both locations, 12.13% of hours contained dolphin signals at only Maui 1, and 11.62% of hours ...
   * at only Lanai 1", McElligott and Lammers 2021, *Front. Mar. Sci.* 8: 703818, pp. 3 and 7, CC BY, measured): their
   * mean, a share of all hours. Heard at all hours, "Dolphin acoustic signals were detected during all hours of the day at
   * Maui 1 except for 1800 and 2000 h", Lanai 1's with "a peak between 0700 and 1700 h" (p. 7), its size in a figure
   * only, so the share is taken as the inshore hours' too (M216's review: put all in them, it had been 2.55 times this).
   * An hour heard is one with a signal in any of its twelve 30-second recordings; resting dolphins "remain fairly
   * silent" (p. 2): the two biases opposed, neither measured (open).
   */
  heardHourShare: (12.13 + 2.7 + 11.62 + 2.7) / 2 / 100,
  /**
   * The recorders' reach, km: the middle of each one's listening range, "between approximately 500 and 2000 m, based on
   * in situ measurements" (p. 3; M216's review: it had been the middle of where the dolphins detected were, "within 1–2
   * km of the coastline").
   */
  reachKm: 1.25,
  /**
   * A group's spread, m: "the median of the bootstrapped group spread mean was 275.88 m" (Olson et al. 2026, p. 7, 15
   * encounters), measured "by estimating the farthest points visible of the group by eye" (p. 5): its width.
   */
  groupSpreadM: 275.88,
  /**
   * Their hours in the bays, hours of the day: "dolphins entered Kealakekua Bay and Kauhako Bay at 07.28 (s.d. ± 28
   * min), and exited again at 16.53 (s.d. ± 41 min)" (Tyne, Johnston, Christiansen and Bejder 2017, *R. Soc. Open Sci.* 4:
   * 160626, p. 4, CC BY, measured at Kona: no Maui Nui hours read).
   */
  inshoreHours: [7 + 28 / 60, 16 + 53 / 60],
  /**
   * Its length, m: the mean of the six Hawaiian spinners Perrin measured, "MMBL 1194 (female, 172 cm)", "Opihi" (male,
   * 185 cm), "01-70-35 (male, 179 cm)", "Momona" (female, 201 cm), "Maka" (female, 183 cm) and "Haina" (sex unknown, 186
   * cm) (Perrin 1975, *Bulletin of the Scripps Institution of Oceanography* 21, p. 148, measured, some of them Sea Life
   * Park's; "Adults reach at least 2 m TL", Norris and Dohl 1980, p. 822). No mean of Hawaii's is published.
   */
  lengthM: (172 + 185 + 179 + 201 + 183 + 186) / 6 / 100,
} as const;

/**
 * The odds a group rests over a hectare of Maui Nui's reef on a day (M216): the share of hours they are heard within
 * the recorders' reach, taken as the share of their hours inshore (`inshoreHours`) one is there, times the share of that
 * reach, a half circle of `reachKm` seaward of the shore, a group covers (a circle `groupSpreadM` wide): one day in 282.
 * The reef's share of their time ("28%", Olson et al. 2026, p. 1) is not put on it: the reef's share of the reach is not
 * read (open).
 */
export function dolphinReefOdds(): number {
  const d = SPINNER_DOLPHIN;
  const group = Math.PI * (d.groupSpreadM / 2) ** 2;
  const reach = (Math.PI * (d.reachKm * 1000) ** 2) / 2;
  return d.heardHourShare * (group / reach);
}

/** A group's dolphins in a hectare it covers: its mean over its width's circle, 10.9. */
export function dolphinsPerGroupHa(): number {
  return (SPINNER_DOLPHIN.groupMean * 1e4) / (Math.PI * (SPINNER_DOLPHIN.groupSpreadM / 2) ** 2);
}

/** Whether a site has the spinner dolphin as read: Maui Nui, where Olson et al. surveyed them. */
export const siteHasSpinnerDolphins = (siteId: SiteId): boolean => siteId === 'maui_nui';
